import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PharmacyOrdersPage from 'pages/PharmacyOrdersPage';
import PharmacyStockPage from 'pages/PharmacyStockPage';
import PharmacyDiscountsPage from 'pages/PharmacyDiscountsPage';
import PharmacyInvoicesPage from 'pages/PharmacyInvoicesPage';
import PharmacyVpayPage from 'pages/PharmacyVpayPage';
import Splash from 'modules/common/components/splash/Splash';
import { status } from 'constants/general';
import initApp from 'utils/functions/init';
import {
  validateAuthCookie,
  getRoleResolutionState,
  getDefaultRouteForRole,
  isRouteAllowedForRole,
  ROLE_RESOLUTION_STATUS,
} from 'utils/functions/authentication';
import LoginRoute from './LoginRoute';

const ProtectedRoute = ({ children, isVpayRoute }) => {
  const isUserAuthenticated = validateAuthCookie();
  const loginInfo = useSelector(state => state.pharmacyAccountManagement.loginInfo);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isUserAuthenticated && loginInfo.status === status.SHOULD_CALL_API) {
      initApp(dispatch);
    }
  }, [dispatch, isUserAuthenticated, loginInfo.status]);

  if (!isUserAuthenticated) {
    return <Navigate replace to="/login" />;
  }

  const { status: resolutionStatus, role } = getRoleResolutionState(loginInfo);

  if (resolutionStatus === ROLE_RESOLUTION_STATUS.LOADING) {
    return <Splash />;
  }

  if (resolutionStatus !== ROLE_RESOLUTION_STATUS.RESOLVED) {
    return <Navigate replace to="/login" />;
  }

  if (!isRouteAllowedForRole(role, isVpayRoute)) {
    return <Navigate replace to={getDefaultRouteForRole(role)} />;
  }

  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  isVpayRoute: PropTypes.bool,
};

ProtectedRoute.defaultProps = {
  isVpayRoute: false,
};

const DefaultLandingRedirect = () => {
  const isUserAuthenticated = validateAuthCookie();
  const loginInfo = useSelector(state => state.pharmacyAccountManagement.loginInfo);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isUserAuthenticated && loginInfo.status === status.SHOULD_CALL_API) {
      initApp(dispatch);
    }
  }, [dispatch, isUserAuthenticated, loginInfo.status]);

  if (!isUserAuthenticated) {
    return <Navigate replace to="/login" />;
  }

  const { status: resolutionStatus, role } = getRoleResolutionState(loginInfo);

  if (resolutionStatus === ROLE_RESOLUTION_STATUS.LOADING) {
    return <Splash />;
  }

  if (resolutionStatus !== ROLE_RESOLUTION_STATUS.RESOLVED) {
    return <Navigate replace to="/login" />;
  }

  return <Navigate replace to={getDefaultRouteForRole(role)} />;
};

const Router = () => (
  <Routes>
    <Route
      exact
      path="/stock"
      element={
        <ProtectedRoute>
          <PharmacyStockPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/orders"
      element={
        <ProtectedRoute>
          <PharmacyOrdersPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/orders/:orderid"
      element={
        <ProtectedRoute>
          <PharmacyOrdersPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/invoices"
      element={
        <ProtectedRoute>
          <PharmacyInvoicesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/discounts"
      element={
        <ProtectedRoute>
          <PharmacyDiscountsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/vpay/*"
      element={
        <ProtectedRoute isVpayRoute>
          <PharmacyVpayPage />
        </ProtectedRoute>
      }
    />
    <Route exact path="/login" element={<LoginRoute />} />
    <Route path="/" element={<DefaultLandingRedirect />} />
    <Route path="*" element={<DefaultLandingRedirect />} />
  </Routes>
);

export default Router;
